import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select'
import { NgxMaskDirective } from 'ngx-mask'
import { take } from 'rxjs/operators'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { CommonService } from '@appShared/services/common.service'
import { ButtonComponent } from '@appShared/components/button/button.component'
import { ShareholderPlanDetailListComponent } from '../../shareholder-plan-detail/shareholder-plan-detail-list/shareholder-plan-detail-list.component'
import { AppFacade } from '@appShared/services/app.facade'
import {
   ShareholderEditSectionData,
   ShareholderEditSection_,
   AdminService
} from '@appShared/services/admin.service'
import { IPlan, IShareholder } from '@appShared/interfaces/[Model-based]'
import { IDomainModel } from '@appShared/services/lookup/[CodeGen]/domain-model.interface'
import { PlanType, PlanTypes } from '@appShared/services/lookup/[CodeGen]/plan-type.domain'

@Component({
   selector: 'app-shareholder-plan-edit',
   templateUrl: './shareholder-plan-edit.component.html',
   // style hack for the dropdown to go outside modal: https://stackoverflow.com/questions/61011313/ng-select-dropdown-is-hidden-by-the-dialog
   styles: [`
      ::ng-deep .ng-dropdown-panel {z-index: 9999999!important;}
   `],
   imports: [
      ReactiveFormsModule,
      NgSelectComponent,
      NgOptionComponent,
      NgxMaskDirective,
      ButtonComponent,
      ShareholderPlanDetailListComponent
   ]
})
export class ShareholderPlanEditComponent implements OnInit {
   @Input() shareholder: IShareholder
   @Input() plan: IPlan
   @Input() canEdit: boolean
   @Input() refreshGrid: () => void
   @Output() shareholderUpdated = new EventEmitter();

   standardDomainSearchFn: (
      term: string,
      item: any,
      matchingFieldOverride1?: string,
      matchingFieldOverride2?: string) => boolean
   selectedPlan: IPlan
   isLoading: boolean
   mouseoverSavePlanSubmit: boolean

   /* form/form-fields */
   planForm: FormGroup
   private _stockPlanBeginingYear: number
   private _currentYear = new Date().getFullYear();
   yearDropdown: FormControl
   years: IDomainModel[] = [];
   planTypeDropdown: FormControl
   planTypes: PlanType[] = PlanTypes;
   shareOffer: FormControl
   sharePrice: FormControl
   isSubmitting: boolean

   constructor(
      private _appFacade: AppFacade,
      private _activeModal: NgbActiveModal,
      private _commonService: CommonService,
      private _adminService: AdminService
   ) {
      this.standardDomainSearchFn = this._commonService.standardDomainSearchFn
   }

   ngOnInit(): void {
      this.isLoading = true

      this._appFacade.siteConfig$.pipe(take(1)).subscribe(siteConfig => {
         this._stockPlanBeginingYear = siteConfig.stockPlanBeginingYear
         this._createFormGroup()
      })
   }

   /*
   * private methods
   * */

   private _createFormGroup(): void {
      for (
         let i = this._stockPlanBeginingYear;
         i <= this._currentYear;
         i++
      ) {
         this.years.push({ code: i, description: i.toString() } as IDomainModel)
      }

      this.yearDropdown = new FormControl(null, Validators.required)
      this.planTypeDropdown = new FormControl(null, Validators.required)
      this.shareOffer = new FormControl(null, Validators.required)
      this.sharePrice = new FormControl(null, Validators.required)

      this.planForm = new FormGroup({
         yearDropdown: this.yearDropdown,
         planTypeDropdown: this.planTypeDropdown,
         shareOffer: this.shareOffer,
         sharePrice: this.sharePrice
      })

      this._setPlanData(this.plan)
   }

   private _setPlanData(selectedPlan?: IPlan): void {
      const setNumber = this._commonService.setNumber
      let year
      let planType: PlanType

      selectedPlan =
         selectedPlan ||
         ({ id: 0, shareholderId: this.plan.shareholderId } as IPlan)

      if (selectedPlan.year) {
         year = this.years.find(yr => selectedPlan.year === yr.code)
      }

      if (selectedPlan.typeCode) {
         planType = this.planTypes.find(
            pType => pType.code === selectedPlan.typeCode
         )
      }

      this.planForm.patchValue({
         yearDropdown: year || this.years[this.years.length - 1],
         planTypeDropdown: planType || null,
         shareOffer: setNumber(selectedPlan.shareOffer),
         sharePrice: setNumber(selectedPlan.sharePrice)
      })

      this.selectedPlan = selectedPlan

      selectedPlan.id ? this.yearDropdown.disable() : this.yearDropdown.enable()

      this.canEdit ? this.planForm.enable() : this.planForm.disable()
   }

   /*
    * public methods
    * */

   savePlan(): void {
      this.isSubmitting = true

      const planFormValues = this.planForm.value
      const typeCode = planFormValues.planTypeDropdown?.code
      const year = planFormValues.yearDropdown?.code

      const updatedPlan = {
         ...this.selectedPlan,
         year,
         typeCode,
         shareOffer: planFormValues.shareOffer,
         sharePrice: planFormValues.sharePrice
      } as IPlan

      this._adminService
         .createUpdatePlan(updatedPlan)
         .then(shareholder => {
            const msgSuffix = updatedPlan.id ? 'updated' : 'created'
            this._commonService.messageUser(`Plan ${msgSuffix}!`)

            let matchingPlan = shareholder.plans.find(plan => plan.year === year)
            this.plan = { ...matchingPlan }

            this._setPlanData(this.plan)

            this.shareholderUpdated.emit({
               shareholderEditSection: ShareholderEditSection_.planInfo,
               shareholder,
               refresh: true
            } as ShareholderEditSectionData)
         })
         .catch(() => {
            /* Do Nothing */
         })
         .finally(() => setTimeout(() => (this.isSubmitting = false), 500))
   }

   shareholderSectionUpdated(
      shareholderEditSectionData: ShareholderEditSectionData
   ): void {
      this.shareholderUpdated.emit(shareholderEditSectionData)
   }
}
